import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import { useAllBook, useBookOrder } from 'store/books/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentOrgPermission, useParentOrgId } from 'store/organizations/selectors';
import { useUserRole } from 'store/users/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { dropdownForResourceType } from 'utils/config/reference';
import { resource_types } from 'store/extraResource/constants';
import { RESOURCE_BOOK_ID_MAP } from 'modules/assignments/helper';
import ResourceTypeSelector from 'modules/extraResource/admin/resourceTypeSelector';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import {
  Z_BUILDER_DIALOG_ABOVE_APP_NAV,
  Z_BUILDER_DIALOG_MENU_THEME_MODAL,
  Z_BUILDER_RESOURCE_TYPE_SELECTOR_MODAL,
} from './assignmentBuilderZIndex';
import { useExams } from 'store/exams/selectors';
import { getCurrenSem } from 'utils/methods/math';

const normalizeSessionValue = (value) => String(value || '').trim().replace(/\//g, '-');
const EXAM_RESOURCE_TYPE = 'exam';

const extractSessionFromText = (value) => {
  const match = String(value || '').match(/(20\d{2})\s*[-/]\s*(20\d{2})/);
  if (!match) return '';
  return `${match[1]}-${match[2]}`;
};

const resolveBookSession = (book) =>
  normalizeSessionValue(
    book?.session ||
      book?.school_session ||
      book?.schoolYear ||
      book?.school_year ||
      extractSessionFromText(book?.description || book?.title || book?.name || ''),
  );

const getSessionFromSemesterKey = (semesterKey) => {
  const match = String(semesterKey || '').match(/semester\s*(1|2)\s*-\s*(\d{4})/i);
  if (!match) return '';
  const sem = Number(match[1]);
  const year = Number(match[2]);
  if (!Number.isFinite(year)) return '';
  if (sem === 1) return `${year}-${year + 1}`;
  return `${year - 1}-${year}`;
};

const AssignmentSourcePickerModal = ({
  open,
  onClose,
  bookId,
  pageId,
  onSelectLocation,
  dialogTitle = 'Select Activity Source',
  defaultResourceType,
  defaultSelectedItems,
  defaultSelectedConfig,
}) => {
  const theme = useTheme();
  const pickerPortalTheme = useMemo(
    () =>
      createTheme({
        ...theme,
        zIndex: {
          ...theme.zIndex,
          modal: Z_BUILDER_DIALOG_MENU_THEME_MODAL,
        },
      }),
    [theme],
  );
  const showAlert = useAlert();
  const booksAllList = useAllBook();
  const currentClass = useCurrentClass();
  const orgPermissions = useCurrentOrgPermission();
  const parentOrgId = useParentOrgId();
  const bookOrder = useBookOrder();
  const { is_super_admin } = useUserRole();
  const exams = useExams();

  const [cloneResourceType, setCloneResourceType] = useState('ebook');
  const [cloneBook, setCloneBook] = useState(bookId || '');
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState({});

  const applyPickerDefaults = useCallback(() => {
    const rt =
      defaultResourceType != null && defaultResourceType !== '' ? defaultResourceType : 'ebook';
    setCloneResourceType(rt);
    if (rt === 'ebook') {
      setCloneBook(String(bookId || ''));
      setSelectedItems([]);
      setSelectedConfig({});
    } else {
      setCloneBook('');
      setSelectedItems(Array.isArray(defaultSelectedItems) ? defaultSelectedItems : []);
      setSelectedConfig(
        defaultSelectedConfig &&
          typeof defaultSelectedConfig === 'object' &&
          !Array.isArray(defaultSelectedConfig)
          ? defaultSelectedConfig
          : {},
      );
    }
  }, [bookId, defaultResourceType, defaultSelectedItems, defaultSelectedConfig]);

  useEffect(() => {
    if (open) applyPickerDefaults();
  }, [open, applyPickerDefaults]);

  const archived_group = useMemo(() => currentClass.archived_group || [], [currentClass]);

  const orderedBook = useMemo(() => {
    let order_group = [];
    const books_group_permission = get(orgPermissions, 'books_group_permission', []);
    (bookOrder || []).forEach((order) => {
      const class_books_group = currentClass.books_group || [];
      if (archived_group.includes(order.key)) return null;
      if (books_group_permission.length == 0 || books_group_permission.includes(order.key)) {
        const has_book = find(class_books_group, (k) => k == order.key);
        if (has_book) {
          const result = [
            ...(order &&
              order.order &&
              order.order
                .map((chapter) => {
                  const book = find(booksAllList, ['id', chapter]);
                  if (book) {
                    return book;
                  }
                  return null;
                })
                .filter(Boolean)),
          ];
          order_group.push({
            key: order.key,
            title: order.title,
            color: order.color,
            books: result,
          });
        }
      }
    });
    return order_group;
  }, [archived_group, bookOrder, booksAllList, currentClass.books_group, orgPermissions]);

  const allowedBooks = useMemo(() => {
    return Array.from(new Set(orderedBook.map((order) => order.books).flat()));
  }, [orderedBook]);

  const availableBooksForClone = useMemo(() => {
    const bks = Array.isArray(booksAllList) ? booksAllList : [];
    if (is_super_admin) {
      return bks.filter((bk) => bk.isNotAssignable === false && bk.active === true);
    }
    return bks.filter((bk) => allowedBooks.find((alb) => alb.id == bk.id));
  }, [booksAllList, allowedBooks, is_super_admin]);

  const currentBookSession = useMemo(() => {
    const matchedBook = (Array.isArray(booksAllList) ? booksAllList : []).find((book) => String(book?.id) === String(bookId));
    return resolveBookSession(matchedBook);
  }, [bookId, booksAllList]);

  const currentSemesterSession = useMemo(() => {
    const currentSemester = getCurrenSem(parentOrgId);
    return normalizeSessionValue(getSessionFromSemesterKey(currentSemester));
  }, [parentOrgId]);

  const examBooksForCurrentSession = useMemo(() => {
    const targetSession = currentSemesterSession || currentBookSession;
    if (!targetSession) return [];
    const examBookIds = new Set();
    (Array.isArray(exams) ? exams : []).forEach((examGroup) => {
      const groupSession = normalizeSessionValue(examGroup?.session);
      if (!groupSession || groupSession !== targetSession) return;
      (Array.isArray(examGroup?.exams) ? examGroup.exams : []).forEach((examItem) => {
        if (examItem?.bookId != null && examItem?.bookId !== '') {
          examBookIds.add(String(examItem.bookId));
        }
      });
    });

    const booksById = new Map((Array.isArray(booksAllList) ? booksAllList : []).map((book) => [String(book?.id), book]));
    return Array.from(examBookIds)
      .map((id) => booksById.get(String(id)))
      .filter(Boolean);
  }, [booksAllList, currentBookSession, currentSemesterSession, exams]);
  const availableBooksWithCurrentSessionExams = useMemo(() => {
    const baseMap = new Map((Array.isArray(availableBooksForClone) ? availableBooksForClone : []).map((book) => [String(book?.id), book]));
    examBooksForCurrentSession.forEach((book) => {
      baseMap.set(String(book?.id), book);
    });
    return Array.from(baseMap.values());
  }, [availableBooksForClone, examBooksForCurrentSession]);

  const isBookUnitType = () => {
    const { balcon, conectándome, comunidad, informate } = resource_types;
    return (
      cloneResourceType === balcon ||
      cloneResourceType === conectándome ||
      cloneResourceType === comunidad ||
      cloneResourceType === informate
    );
  };

  const handleResourceTypeChange = (value) => {
    setCloneResourceType(value);
    setSelectedItems([]);
    setSelectedConfig({});
    if (value === 'ebook') {
      setCloneBook(bookId || '');
    } else if (value === EXAM_RESOURCE_TYPE) {
      setCloneBook('');
    } else {
      setCloneBook('');
    }
  };

  const getBookIdForResourceType = () => {
    if (cloneResourceType === 'ebook' || cloneResourceType === EXAM_RESOURCE_TYPE) {
      return cloneBook;
    }
    if (isBookUnitType()) {
      return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
    }
    if (cloneResourceType === resource_types.AP && selectedConfig?.selectedCategoryAP?.chapter_ids) {
      return selectedConfig.selectedCategoryAP.chapter_ids;
    }
    if (cloneResourceType === resource_types.story && selectedItems && selectedItems.length > 0) {
      const storyItem = selectedItems[0];
      if (storyItem.bookId) {
        return storyItem.bookId;
      }
    }
    if (cloneResourceType === resource_types.resource && selectedItems && selectedItems.length > 0) {
      return selectedItems[0].id || null;
    }
    if (selectedItems && selectedItems.length > 0) {
      const firstItem = selectedItems[0];
      if (firstItem.bookId) {
        return firstItem.bookId;
      }
    }
    return RESOURCE_BOOK_ID_MAP[cloneResourceType] || null;
  };

  const handleClose = () => {
    applyPickerDefaults();
    onClose();
  };

  const isFormValid = () => {
    if (cloneResourceType === 'ebook' || cloneResourceType === EXAM_RESOURCE_TYPE) {
      return cloneBook;
    }
    return selectedItems && selectedItems.length > 0;
  };

  const handleSelect = () => {
    if (cloneResourceType === 'ebook' || cloneResourceType === EXAM_RESOURCE_TYPE) {
      if (!cloneBook) {
        showAlert('Please select book', 'error');
        return;
      }
    } else {
      if (!selectedItems || selectedItems.length === 0) {
        showAlert('Please select a resource', 'error');
        return;
      }
    }

    const bookIdValue = getBookIdForResourceType() || bookId;

    if (!bookIdValue) {
      showAlert('Missing book id', 'error');
      return;
    }

    onSelectLocation({
      bookId: String(bookIdValue),
      resourceType: cloneResourceType,
      items: selectedItems,
      selectedConfig: selectedConfig,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: Z_BUILDER_DIALOG_ABOVE_APP_NAV }}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <ThemeProvider theme={pickerPortalTheme}>
        <DialogContent>
          <div style={{ marginTop: '16px' }}>
            <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
              Select resource type, then choose a book or resource to load activities and quizzes.
            </p>
            <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
              <InputLabel>Resource Type</InputLabel>
              <Select value={cloneResourceType} label="Resource Type" onChange={(e) => handleResourceTypeChange(e.target.value)}>
                <MenuItem value="ebook">Ebook</MenuItem>
                {map(dropdownForResourceType, (element, i) => (
                  <MenuItem key={i} value={element?.value}>
                    {element?.label}
                  </MenuItem>
                ))}
                <MenuItem value={EXAM_RESOURCE_TYPE}>Exams</MenuItem>
              </Select>
            </FormControl>

            {cloneResourceType === 'ebook' || cloneResourceType === EXAM_RESOURCE_TYPE ? (
              <>
                <FormControl variant="outlined" fullWidth style={{ marginBottom: 16 }}>
                  <InputLabel>Book</InputLabel>
                  <Select
                    value={cloneBook}
                    label="Book"
                    onChange={(e) => {
                      setCloneBook(e.target.value);
                    }}
                  >
                    {(cloneResourceType === EXAM_RESOURCE_TYPE ? examBooksForCurrentSession : availableBooksWithCurrentSessionExams).map((bk) => (
                      <MenuItem key={bk.id} value={bk.id}>
                        {bk.description || bk.title || bk.name || `Book ${bk.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <ResourceTypeSelector
                  data={selectedItems}
                  type={cloneResourceType}
                  config={selectedConfig}
                  modalZIndex={Z_BUILDER_RESOURCE_TYPE_SELECTOR_MODAL}
                  handleSubmit={(item, additionalResource) => {
                    setSelectedItems(item);
                    setSelectedConfig({
                      ...selectedConfig,
                      ...(additionalResource &&
                        cloneResourceType === resource_types.AP && {
                          selectedCategoryAP: additionalResource,
                        }),
                      ...(additionalResource &&
                        cloneResourceType === resource_types.resource && {
                          linkResource: additionalResource,
                        }),
                      ...(additionalResource &&
                        cloneResourceType === resource_types.story && {
                          level: additionalResource,
                        }),
                    });
                  }}
                  pageId={pageId}
                />
                {selectedItems && selectedItems.length > 0 && (
                  <div style={{ marginTop: 16, padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>Selected Resource:</p>
                    {isBookUnitType() || cloneResourceType === resource_types.AP ? (
                      <p style={{ fontSize: '14px' }}>{selectedItems[0]?.shortName || selectedItems[0]?.displayName}</p>
                    ) : cloneResourceType === resource_types.resource ? (
                      <p style={{ fontSize: '14px' }}>{selectedItems[0]?.displayName}</p>
                    ) : cloneResourceType === resource_types.story ? (
                      <p style={{ fontSize: '14px' }}>{selectedItems[0]?.title}</p>
                    ) : (
                      <p style={{ fontSize: '14px' }}>{selectedItems[0]?.shortName || selectedItems[0]?.displayName}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSelect} disabled={!isFormValid()}>
            Select
          </Button>
        </DialogActions>
      </ThemeProvider>
    </Dialog>
  );
};

export default AssignmentSourcePickerModal;
